import ReactECharts from 'echarts-for-react';

/**
 * RaisyStackChart_4 — CONTENT ALTERNATIVE to RaisyStackChart (sunburst).
 *
 * Angle: "one entity becomes a whole stack". Instead of the sunburst's
 * structure view, this variant makes the MULTIPLICITY the finding:
 * one horizontal bar per stack layer, sized by the number of blocks
 * at that layer (Entity 1, Processes 2, Bots 3, Kernels 9), so the
 * reader sees the top-down fan-out at a glance.
 *
 * ZERO invented numbers: every count is computed by walking the exact
 * same hierarchy the original sunburst renders (stackData below is a
 * verbatim copy of RaisyStackChart's data; counts are derived with
 * plain recursion — see countByLayer).
 *
 * Chart-type-first: the concept is a COMPARISON of counts across four
 * discrete layers → horizontal bars.
 */

const BRAND = {
  tealDark: '#087B8B',
  teal: '#0AB3BF',
  tealLight: '#4DD4E0',
  info: '#3B82F6',
  slate: '#334155',
  muted: '#64748B',
  surface: '#FFFFFF',
};

interface StackNode {
  name: string;
  value?: number;
  itemStyle?: { color: string };
  children?: StackNode[];
}

/** Verbatim copy of the hierarchy data in RaisyStackChart.tsx. */
const stackData: StackNode[] = [
  {
    name: 'Entity',
    itemStyle: { color: BRAND.tealDark },
    children: [
      {
        name: 'Process',
        itemStyle: { color: BRAND.teal },
        children: [
          {
            name: 'Bot',
            itemStyle: { color: BRAND.tealLight },
            children: [
              { name: 'Kernel', value: 1, itemStyle: { color: BRAND.info } },
              { name: 'Kernel', value: 1, itemStyle: { color: BRAND.info } },
              { name: 'Kernel', value: 1, itemStyle: { color: BRAND.info } },
            ],
          },
          {
            name: 'Bot',
            itemStyle: { color: BRAND.tealLight },
            children: [
              { name: 'Kernel', value: 1, itemStyle: { color: BRAND.info } },
              { name: 'Kernel', value: 1, itemStyle: { color: BRAND.info } },
            ],
          },
        ],
      },
      {
        name: 'Process',
        itemStyle: { color: BRAND.teal },
        children: [
          {
            name: 'Bot',
            itemStyle: { color: BRAND.tealLight },
            children: [
              { name: 'Kernel', value: 1, itemStyle: { color: BRAND.info } },
              { name: 'Kernel', value: 1, itemStyle: { color: BRAND.info } },
              { name: 'Kernel', value: 1, itemStyle: { color: BRAND.info } },
              { name: 'Kernel', value: 1, itemStyle: { color: BRAND.info } },
            ],
          },
        ],
      },
    ],
  },
];

/** Level description, same wording as the original chart's levelMeta. */
const levelMeta: Record<string, string> = {
  Entity: 'Who acts — companies, products, people',
  Process: 'What they do repeatedly — workflows in a domain',
  Bot: 'Who executes each step — bots or humans',
  Kernel: 'What the actor knows — rules, context, schema',
};

const LAYER_ORDER = ['Entity', 'Process', 'Bot', 'Kernel'] as const;
const LAYER_COLORS: Record<string, string> = {
  Entity: BRAND.tealDark,
  Process: BRAND.teal,
  Bot: BRAND.tealLight,
  Kernel: BRAND.info,
};

/**
 * Derivation (visible): walk the real hierarchy and count the nodes at
 * each layer. Entity = 1 root; Process/Bot = children at that depth;
 * Kernel = leaf values summed (each leaf carries value 1 in the data).
 */
const countByLayer = (nodes: StackNode[], counts: Record<string, number>) => {
  for (const node of nodes) {
    counts[node.name] = (counts[node.name] ?? 0) + (node.value ?? 1);
    if (node.children) countByLayer(node.children, counts);
  }
  return counts;
};

const counts = countByLayer(stackData, {});
const layers = LAYER_ORDER.map((name) => ({ name, count: counts[name] ?? 0 }));
const maxCount = Math.max(...layers.map((l) => l.count));

/** Pluralized block name for a given count (1 Entity, 2 Processes, 3 Bots, 9 Kernels). */
const pluralOf = (name: string, count: number) => {
  if (count === 1) return name;
  if (name === 'Process') return 'Processes';
  return `${name}s`;
};

/** ONE shared formatter used by tooltip, y-axis labels, and bar data labels. */
const formatLayer = (name: string, count: number) => `${count} ${pluralOf(name, count)}`;

export const RaisyStackChart_4 = ({ height = 380 }: { height?: number }) => {
  const option = {
    backgroundColor: BRAND.surface,
    grid: { left: 8, right: 88, top: 28, bottom: 8, containLabel: true },
    tooltip: {
      trigger: 'item',
      backgroundColor: '#1E293B',
      borderColor: BRAND.slate,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (p: { name: string; value: number }) =>
        `${formatLayer(p.name, p.value)}<br/><span style="color:#94A3B8">${levelMeta[p.name] ?? 'Raisy building block'}</span>`,
    },
    xAxis: {
      type: 'value',
      max: maxCount,
      axisLabel: {
        color: BRAND.muted,
        fontSize: 12,
        // plain numbers — the unit is stated by the y-axis and bar labels;
        // long "N Kernels" labels overlap when the card renders in a narrow column
        formatter: (v: number) => `${v}`,
      },
      splitLine: { lineStyle: { color: '#E2E8F0' } },
    },
    yAxis: {
      type: 'category',
      inverse: true,
      data: layers.map((l) => l.name),
      axisTick: { show: false },
      axisLine: { show: false },
      axisLabel: {
        color: BRAND.slate,
        fontSize: 13,
        fontWeight: 700,
        formatter: (name: string) => formatLayer(name, counts[name] ?? 0),
      },
    },
    series: [
      {
        type: 'bar',
        data: layers.map((l) => ({
          name: l.name,
          value: l.count,
          itemStyle: { color: LAYER_COLORS[l.name], borderRadius: [0, 6, 6, 0] },
        })),
        barWidth: 34,
        label: {
          show: true,
          position: 'right',
          color: BRAND.slate,
          fontSize: 13,
          fontWeight: 700,
          formatter: (p: { name: string; value: number }) => formatLayer(p.name, p.value),
        },
      },
    ],
  };

  return (
    <div className="my-8 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 text-center">
        Figure 2. One entity becomes a whole stack: the same company counted layer by layer fans out from a single
        Entity to 2 Processes, 3 Bots, and 9 Kernels — the deeper the layer, the more blocks it takes to run one
        company. Hover a bar for what that layer means.
      </p>
    </div>
  );
};
